import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { TokenStorageService } from './token-storage.service';
import { CorrelationIdService } from './correlation-id.service';
import { csrfHeadersForFetch } from '../_helpers/xsrf.util';

export interface ClientFirmaLogPayload {
  evento: string;
  tramiteId?: number | string;
  origen?: string;
  errorType?: string;
  errorMessage?: string;
}

/** Cuerpo serializado enviado al backend (todos los campos opcionales salvo evento). */
interface ClientFirmaLogBody {
  evento: string;
  tramiteId?: string;
  origen?: string;
  errorType?: string;
  errorMessage?: string;
}

/**
 * Telemetría de firma Autofirma — fire-and-forget, totalmente aislada del flujo de negocio.
 * Usa fetch nativo (no HttpClient) para evitar que RxJS cancele la petición al desuscribirse,
 * lo que en DevTools aparece como "(canceled)" aunque el servidor ya haya respondido 200.
 */
@Injectable({
  providedIn: 'root'
})
export class ClientFirmaLogService {

  private readonly API_URL = `${environment.API_URL}/client-events/firma`;

  constructor(
    private tokenStorage: TokenStorageService,
    private correlationIdService: CorrelationIdService
  ) {}

  /** Nunca lanza excepciones ni bloquea el hilo del llamador. */
  log(payload: ClientFirmaLogPayload): void {
    try {
      const body = this.normalizePayload(payload);
      if (!body.evento) {
        return;
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Correlation-Id': this.correlationIdService.getCorrelationId(),
        ...csrfHeadersForFetch(),
      };
      const token = this.tokenStorage.getToken();
      if (token) {
        headers['accessToken'] = token;
        headers['Authorization'] = `Bearer ${token}`;
      }

      // keepalive: la petición puede completarse aunque el usuario navegue o cierre el modal.
      void fetch(this.API_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        keepalive: true
      }).catch(() => {});
    } catch {
      // Telemetría opcional: cualquier fallo se ignora silenciosamente.
    }
  }

  iniciada(tramiteId: number | string | undefined, origen: string): void {
    this.safe(() => this.log({ evento: 'AUTOFIRMA_INICIADA', tramiteId, origen }));
  }

  error(
    tramiteId: number | string | undefined,
    origen: string,
    errorType?: string,
    errorMessage?: string
  ): void {
    this.safe(() => this.log({ evento: 'AUTOFIRMA_ERROR', tramiteId, origen, errorType, errorMessage }));
  }

  cancelada(
    tramiteId: number | string | undefined,
    origen: string,
    errorType?: string,
    errorMessage?: string
  ): void {
    this.safe(() => this.log({ evento: 'AUTOFIRMA_CANCELADA', tramiteId, origen, errorType, errorMessage }));
  }

  certificadoInvalido(tramiteId: number | string | undefined, origen: string): void {
    this.safe(() => this.log({ evento: 'AUTOFIRMA_CERTIFICADO_INVALIDO', tramiteId, origen }));
  }

  exito(tramiteId: number | string | undefined, origen: string): void {
    this.safe(() => this.log({ evento: 'AUTOFIRMA_EXITO', tramiteId, origen }));
  }

  subidaOk(tramiteId: number | string | undefined, origen: string): void {
    this.safe(() => this.log({ evento: 'AUTOFIRMA_SUBIDA_OK', tramiteId, origen }));
  }

  subidaError(tramiteId: number | string | undefined, origen: string, errorMessage?: string): void {
    this.safe(() => this.log({ evento: 'AUTOFIRMA_SUBIDA_ERROR', tramiteId, origen, errorMessage }));
  }

  noDisponible(tramiteId: number | string | undefined, origen: string): void {
    this.safe(() => this.log({ evento: 'AUTOFIRMA_NO_DISPONIBLE', tramiteId, origen }));
  }

  private safe(action: () => void): void {
    try {
      action();
    } catch {
      // Sin efecto en el flujo de firma.
    }
  }

  private normalizePayload(payload: ClientFirmaLogPayload): ClientFirmaLogBody {
    const result: ClientFirmaLogBody = {
      evento: this.toSafeString(payload.evento)
    };
    const tramiteId = this.toSafeString(payload.tramiteId);
    if (tramiteId) {
      result.tramiteId = tramiteId;
    }
    const origen = this.toSafeString(payload.origen);
    if (origen) {
      result.origen = origen;
    }
    const errorType = this.toSafeString(payload.errorType);
    if (errorType) {
      result.errorType = errorType;
    }
    const errorMessage = this.toSafeString(payload.errorMessage);
    if (errorMessage) {
      result.errorMessage = errorMessage;
    }
    return result;
  }

  private toSafeString(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value).replace(/[\r\n\t]/g, ' ').trim().slice(0, 500);
  }
}
